import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';
import { ContragentDeleteQueryInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-delete-query.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { ContragentFormInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-form.dto';
import { ContragentFormDto } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-form.dto';
import type { PersonFormInterface } from '@zaoblako/shared/modules/client/dtos/person/person-form.dto';
import { JurisdictionTypeEnum } from '@zaoblako/shared/modules/client/enums/jurisdiction-type.enum';
import { PersonTypeEnum } from '@zaoblako/shared/modules/client/enums/person.type.enum';
import { BankService } from '@zaoblako/web/core/services/catalog/bank.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ContragentDeletingModalComponent } from '@zaoblako/web/modules/client/dumbs/contragent-form/contragent-deleting-modal/contragent-deleting-modal.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { InnAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/inn-auto-complete/inn-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { ActivitySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/activity-single-select/activity-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableBankDetailsComponent } from '@zaoblako/web/shared/dumbs/table-bank-details/table-bank-details.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { JurisdictionTypeNamePipe } from '@zaoblako/web/shared/pipes/jurisdiction-type-name.pipe';
import { PersonTypeNamePipe } from '@zaoblako/web/shared/pipes/person-type-name.pipe';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';
import { FdoAccessService } from '@zaoblako/web/core/models/enums/access-rules/fdo/fdo-access.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { FinanceAccessRulesKeyEnum } from '@zaoblako/web/core/models/enums/access-rules/finance/finance-access-rules-key.enum';
import { FinanceAccessRulesConst } from '@zaoblako/web/core/models/enums/access-rules/finance/finance-access-rules.const';

@Component({
  selector: 'crm-contragent-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    ButtonComponent,
    CardModule,
    InnAutoCompleteComponent,
    InputMaskModule,
    InputTextComponent,
    JoinPipe,
    PersonTypeNamePipe,
    SingleSelectComponent,
    TableBankDetailsComponent,
    FormsModule,
    ActivitySingleSelectComponent,
    ClientAutoCompleteComponent,
    JurisdictionTypeNamePipe,
    CheckboxModule,
    InputNumberModule
  ],
  templateUrl: './contragent-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContragentFormComponent extends BaseFormComponent<ContragentFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public contragent = <ContragentFormInterface>{};

  public dto = ContragentFormDto;
  public person: PersonFormInterface;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public banks: BankFindInterface[] = [];
  public persons: PersonFormInterface[] = [];

  public personTypes: PersonTypeEnum[] = Object.values(PersonTypeEnum);
  public jurisdictions: JurisdictionTypeEnum[] = Object.values(JurisdictionTypeEnum);

  private statistics = { total: 0, representedTenants: 0 };

  public isSalaryControlAvailable = false;

  public get JurisdictionTypeEnum() {
    return JurisdictionTypeEnum;
  }

  private ref: DynamicDialogRef;

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly dialogService: DialogService,
    private readonly bankService: BankService,
    private readonly contragentService: ContragentService,
    private readonly companyService: CompanyService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    public readonly fdoAccessService: FdoAccessService,
    public readonly globalService: GlobalService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.isSalaryControlAvailable = this.globalService.isAccessRule(
      FinanceAccessRulesConst[FinanceAccessRulesKeyEnum.FINANCE_DOCUMENT_SALARY_ACCRUAL_READ]
    );
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    const tenant = this.formStoreService.getState.forms[this.index]?.data?.tenant;
    if (tenant) {
      this.contragent.representedTenant = tenant;
    }

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();

      this.contragentService
        .getRelatedObjectsStat(this.formStoreService.getState.forms[this.index]?.entityId)
        .subscribe((stat) => {
          this.statistics = { total: stat.total, representedTenants: stat.representedTenants };
        });
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.contragentService
      .findById<ContragentFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (contragent) => {
          this.contragent = contragent;
          if (this.contragent?.inn) {
            this.companyService.externalSearch({ query: this.contragent.inn }).subscribe((persons) => {
              this.person = persons[0] ?? <PersonFormInterface>{ inn: this.contragent.inn };
              this.cdRef.markForCheck();
            });
          }
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveContragent() {
    // this.contragent.contactPersons?.forEach((itemPerson) => {
    //   if (itemPerson.phone?.length && itemPerson.phone?.[0] !== '+') {
    //     itemPerson.phone = '+' + itemPerson.phone;
    //   }
    // });

    if (this.checkValidate(this.contragent).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.contragentService.update<ContragentFormInterface, ContragentFindInterface>(this.contragent).subscribe({
          next: (contragent) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Контрагент "${this.contragent?.name}" обновлен`);
            this.formStoreService.saveNext(contragent, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.contragentService.create<ContragentFindInterface>(this.contragent).subscribe({
          next: (contragent) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Контрагент создан');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/client/contragent/card/' + contragent.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(contragent, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteContragent() {
    if (this.statistics.representedTenants > 0 || this.statistics.total > 0) {
      this.ref = this.dialogService.open(ContragentDeletingModalComponent, {
        header: 'Удаление контрагента',
        contentStyle: { overflow: 'auto' },
        styleClass: 'w-4',
        focusOnShow: false,
        data: {
          previousContragentId: this.formStoreService.getState.forms[this.index]?.entityId
        }
      });

      this.ref.onClose.subscribe((contragent: ContragentFindInterface) => {
        if (contragent) {
          deletingContragent({ delegateTo: contragent?.id });
        }
      });
    } else {
      this.confirmService.confirm({
        message: `Вы действительно хотите удалить контрагента "${this.contragent?.name}"?`,
        accept: () => {
          deletingContragent();
        }
      });
    }

    const deletingContragent = (params: ContragentDeleteQueryInterface = {}) => {
      this.formStoreService.setLoading(true, this.index);
      this.cdRef.markForCheck();

      this.contragentService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId, params).subscribe({
        next: () => {
          this.errorService.addSuccessMessage(`Контрагент "${this.contragent?.name}" удален!`);
          this.formStoreService.deleteNext(this.index);
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
    };
  }

  public selectedPerson(person: PersonFormInterface) {
    if (person) {
      if (typeof person === 'object') {
        this.contragent = {
          ...this.contragent,
          name: person.name,
          personType: person.personType,
          manager: person.manager,
          email: person.email,
          phone: person.phone,
          website: person.website,
          address: person.address,
          postAddress: person.postAddress,
          kpp: person.kpp,
          ogrn: person.ogrn,
          inn: person.inn
        };
      } else {
        this.contragent.inn = person;
      }
    }
  }

  public searchBank(name: string) {
    this.bankService.search({ search: name }).subscribe((banks) => {
      this.banks = banks;
      this.cdRef.markForCheck();
    });
  }

  public searchParson(name: string) {
    this.companyService.externalSearch({ query: name }).subscribe((persons) => {
      this.persons = persons;
      this.cdRef.markForCheck();
    });
  }
}
